import { Controller } from "@hotwired/stimulus";
import { acceptListToString, isValidType } from "../javascripts/is_valid_type";
import heic2any from "heic2any";

/**
 * ファイルアップロードボタン
 * @example
 * <div data-controller="upload-button">
 *  <input type="file" data-upload-button-target="input" hidden>
 *  <button data-action="click->upload-button#input">ファイルを選択</button>
 *  以下は任意
 *  <img data-upload-button-target="image" hidden>
 *  <span data-upload-button-target="filename" data-default-text="選択されていません">選択されていません</span>
 *  <input type="hidden" data-upload-button-target="removeInput" name="remove_image" value="0">
 * </div>
 */
export class UploadButtonController extends Controller {
  static targets = [
    "input",
    "filename",
    "image",
    "imageContainer",
    "button",
    // 以下は編集時に使用
    "removeInput",
  ];
  static values = {
    // ファイルがある場合にボタンを無効にする
    disableButton: { type: Boolean, default: false },
    // 以下は編集時に使用
    imageUrl: String,
    filename: String,
    removed: Boolean,
  };

  #file = null;
  #url = null;
  #filename = null;
  #removed = false;
  #isPresent = false;

  connect() {
    this.inputTarget.addEventListener("change", this.#onChange);
    for (const img of this.imageTargets) {
      img.addEventListener("error", this.#handleImageError);
    }
    this.#url = this.imageUrlValue;
    this.#filename = this.filenameValue;
    this.#removed = this.removedValue;
    this.#update(true);
  }

  input() {
    this.inputTarget.click();
  }

  delete() {
    this.inputTarget.value = null;
    this.#removed = true;
    this.#update();
  }

  #onChange = () => {
    this.#file = this.inputTarget.files[0];
    this.#removed = false;
    this.#update();
  };

  #update(isConnect = false) {
    if (this.#removed || (!this.#isValid() && !isConnect)) {
      this.#file = null;
      this.#url = null;
      this.#filename = null;
    }
    if (this.#file) {
      this.#filename = this.#file.name;
      if (!this.#url?.startsWith("blob:")) {
        this.#url = URL.createObjectURL(this.#file);
      }
    }
    this.#isPresent = !!this.#url;
    console.debug("update", {
      file: this.#file,
      url: this.#url,
      filename: this.#filename,
      removed: this.#removed,
      isPresent: this.#isPresent,
    });
    this.#render();
  }

  #isValid() {
    if (!this.#file) return false;

    // acceptと比較
    const accept = this.inputTarget.accept;
    if (!isValidType(this.#file, accept)) {
      this.inputTarget.value = ""; // 入力をクリア
      const acceptList = acceptListToString(accept);
      alert(`ファイル形式は${acceptList} 形式の画像を設定してください。`);
      return false;
    }

    // サイズ制限
    const maxSize = this.inputTarget.dataset.maxSize;
    const size = this.#file.size;
    if (maxSize && maxSize < size) {
      const mb = maxSize / 1024 / 1024;
      this.inputTarget.value = ""; // 入力をクリア
      alert(`${mb}MB以内でアップロードしてください。`);
      return false;
    }
    return true;
  }

  #render() {
    // ファイル名
    if (this.hasFilenameTarget) {
      if (this.#filename) {
        this.filenameTarget.textContent = this.#filename;
      } else {
        const text = this.filenameTarget.dataset.defaultText || "選択されていません";
        this.filenameTarget.textContent = text;
      }
    }
    // 画像プレビュー
    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.hidden = !this.#url;
    }
    if (this.hasImageTarget) {
      if (this.#url) {
        this.imageTarget.src = this.#url;
        this.imageTarget.hidden = false;
      } else {
        this.imageTarget.hidden = true;
      }
    }
    // ボタンの無効化
    if (this.disableButtonValue && this.hasButtonTarget) {
      this.buttonTarget.disabled = this.#isPresent;
      this.buttonTarget.ariaDisabled = this.#isPresent;
    }
    // removeInputの更新
    if (this.hasRemoveInputTarget) {
      this.removeInputTarget.value = this.#removed ? "1" : "0";
    }
  }

  // 画像の読み込みエラー時、HEICの場合は変換する
  #handleImageError = async () => {
    const filename = this.#file?.name;
    if (!filename) return;
    if (!filename.toLowerCase().endsWith(".heic")) return;

    // 壊れた画像表示を消す
    this.#url = `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg"><rect fill="white" width="100%" height="100%"/></svg>`,
    )}`;
    this.#render();

    const heic = await heic2any({ blob: this.#file });
    this.#file = heic;
    this.#url = URL.createObjectURL(heic);
    this.#render();
  };
}
